import { type ColumnDef, type Table, type Row } from '@tanstack/react-table';
import { DateValueRaw } from './Value';
import { type Settings, type ViewerBoardItem, type ViewerAsset } from './types';
import { IMAGE_EXTENSIONS } from './Value';
import { getLabel } from './utils';

type AssetsById = Record<string, ViewerAsset>;

type EventsItemProps = {
  row: Row<ViewerBoardItem>;
  settings: Settings;
  assetsById: AssetsById;
  previewTumbnailGeneratorUrl?: string;
};

function EventsItem({ row, settings, assetsById, previewTumbnailGeneratorUrl }: EventsItemProps) {
  let cardImage = null;
  let headline = null;
  let description = null;
  let location = null;
  let date = null;
  let time = null;
  let learnMore = null;

  if (settings.eventsItemImageColumn && (row.original.values[settings.eventsItemImageColumn]?.value as any)?.asset_ids?.length) {
    const assetIds: string[] = (row.original.values[settings.eventsItemImageColumn]?.value as any)?.asset_ids || [];
    const imageAssetId = assetIds.find((assetId) => {
      const asset = assetsById[assetId];

      if (!asset) {
        return false;
      }

      return IMAGE_EXTENSIONS.includes(asset.extension);
    });

    if (imageAssetId) {
      const asset = assetsById[imageAssetId];

      if (asset._thumbnails) {
        const thumbnailUrl = asset._thumbnails['1:AUTO_MEDIUM'];

        cardImage = (
          <div className="events-image" data-format="1:AUTO_MEDIUM">
            <img src={`https://thumbnails.getgorilla.app${thumbnailUrl}`} width="" height="" alt="" />
          </div>
        );
      } else {
        cardImage = (
          <div className="events-image" data-format="1:AUTO_MEDIUM">
            <img
              src={`${previewTumbnailGeneratorUrl}?mode=1:AUTO_MEDIUM&source=${encodeURIComponent(asset.public_url!)}`}
              width=""
              height=""
              alt=""
            />
          </div>
        );
      }
    }
  }

  if (settings.eventsItemImageColumn && !cardImage) {
    cardImage = (
      <div className="SkeletonImage" data-format="1:AUTO_MEDIUM">
        <div style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-image"></i>
          <p style={{ marginTop: '20px', fontSize: '14px' }}>{getLabel('no_image', settings)}</p>
        </div>
      </div>
    );
  }

  if (settings.eventsItemHeadlineColumn) {
    let headlineText = null;

    if (settings.eventsItemHeadlineColumn === 'name') {
      headlineText = row.original.name;
    } else if ((row.original.values[settings.eventsItemHeadlineColumn]?.value as any).text) {
      headlineText = (row.original.values[settings.eventsItemHeadlineColumn]?.value as any).text;
    }

    if (headlineText) {
      headline = (
        <div className="events-headline">
          <h2>{headlineText}</h2>
        </div>
      );
    }
  }

  if (settings.eventsItemDescriptionColumn) {
    if ((row.original.values[settings.eventsItemDescriptionColumn]?.value as any).text) {
      description = (
        <div className="events-description">
          <p style={{ whiteSpace: 'pre-wrap' }}>{(row.original.values[settings.eventsItemDescriptionColumn]?.value as any).text}</p>
        </div>
      );
    }
  }

  if (settings.eventsItemLocationColumn) {
    if ((row.original.values[settings.eventsItemLocationColumn]?.value as any).text) {
      location = (
        <div className="events-location">
          <strong>{getLabel('location', settings)}</strong>
          <p>
            <a
              href={`https://maps.google.com/maps?q=${(row.original.values[settings.eventsItemLocationColumn]?.value as any).text}`}
              target="_blank"
            >
              {(row.original.values[settings.eventsItemLocationColumn]?.value as any).text}
            </a>
          </p>
        </div>
      );
    }
  }

  if (settings.eventsItemDateColumn) {
    if ((row.original.values[settings.eventsItemDateColumn]?.value as any).text) {
      date = (
        <div className="events-date">
          <strong>{getLabel('date', settings)}</strong>
          <p>
            <DateValueRaw
              value={(row.original.values[settings.eventsItemDateColumn]?.value as any).text}
              dateFormat={settings.eventsDateFormat || 'YYYY-MM-DD'}
              timeFormat={settings.eventsTimeFormat || '24h'}
            />
          </p>
        </div>
      );
    }
  }

  if (settings.eventsItemLearnMoreColumn) {
    if ((row.original.values[settings.eventsItemLearnMoreColumn]?.value as any).url) {
      learnMore = (
        <a
          className="LearnMoreButton"
          href={`${(row.original.values[settings.eventsItemLearnMoreColumn]?.value as any).url}`}
          target="_blank"
        >
          {getLabel('learn_more', settings)}
        </a>
      );
    }
  }

  return (
    <div className={settings.eventsItemPadding === 'none' ? 'events-item events-item--no_padding' : 'events-item'}>
      <div className="events-header">
        {cardImage}
        <div className="events-preview">
          {headline}
          {date || time || location ? (
            <div className="events-meta">
              {date}
              {time}
              {location}
            </div>
          ) : null}
          {description}
          {learnMore}
        </div>
      </div>
    </div>
  );
}

type EventsProps = {
  mode: 'preview' | 'publish';
  settings: Settings;
  assetsById: AssetsById;
  noBoardItems: boolean;
  columns: ColumnDef<ViewerBoardItem, string>[];
  previewTumbnailGeneratorUrl?: string;
  table: Table<ViewerBoardItem>;
};

export function Events({ table, settings, assetsById, previewTumbnailGeneratorUrl, noBoardItems }: EventsProps) {
  const items = table.getRowModel().rows;

  return (
    <div className={`events`}>
      {/* content-alignment-${settings.textAlignment} */}
      {table.getRowModel().rows.length === 0 ? (
        <div style={{ paddingBottom: '20px', textAlign: 'left' }}>
          {getLabel(noBoardItems ? 'nothing_to_show' : 'no_results_found', settings)}
        </div>
      ) : (
        <>
          {items.map((item, idx) => (
            <EventsItem
              key={idx}
              row={item}
              settings={settings}
              assetsById={assetsById}
              previewTumbnailGeneratorUrl={previewTumbnailGeneratorUrl}
            />
          ))}
        </>
      )}
    </div>
  );
}
